import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { getUsageLimits, setFounderType as apiSetFT } from "../lib/api";
import AppShell from "./AppShell";

const FOUNDER_TYPES = [
  {
    key: "first_time",
    title: "First-time founder",
    description:
      "Starting your first thing. I'll push you harder on core assumptions.",
  },
  {
    key: "technical",
    title: "Technical builder",
    description: "You can ship. I'll push you on distribution, not features.",
  },
  {
    key: "non_technical",
    title: "Non-technical",
    description:
      "You move through conversations. I'll push scrappy validation.",
  },
  {
    key: "repeat",
    title: "Repeat founder",
    description: "You've done this before. I'll skip basics and go strategic.",
  },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [selectedType, setSelectedType] = useState(user?.founder_type || null);
  const [saving, setSaving] = useState(false);
  const [limits, setLimits] = useState(null);

  useEffect(() => {
    setSelectedType(user?.founder_type || null);
  }, [user?.founder_type]);

  useEffect(() => {
    getUsageLimits()
      .then((data) => setLimits(data.diagnosis))
      .catch(() => setLimits(null));
  }, []);

  const handleSave = async () => {
    if (!selectedType || selectedType === user?.founder_type) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await apiSetFT(selectedType);
      await refresh();
      setEditing(false);
    } catch (error) {
      console.error("Failed to update founder type:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedType(user?.founder_type || null);
    setEditing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "Not yet";
    return new Date(dateStr).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    });
  };

  const formatDuration = (seconds = 0) => {
    if (seconds <= 0) return "Ready now";
    const mins = Math.ceil(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const rest = mins % 60;
    return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
  };

  const currentTypeObj = FOUNDER_TYPES.find(
    (t) => t.key === user?.founder_type,
  );
  const hasUnlimitedAccess = !!limits?.unlimited;

  if (!user) {
    return (
      <AppShell>
        <div className="overline">loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl fade-up">
        <div className="mb-10">
          <div className="overline mb-2">profile</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
            Your details.
          </h1>
        </div>

        {/* Basic Info Card */}
        <div className="card p-8 mb-6">
          <div className="flex items-start gap-6 mb-8">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-20 h-20 rounded-full border-2 border-zinc-200"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-zinc-100 border-2 border-zinc-200 flex items-center justify-center">
                <span className="text-2xl font-display font-semibold text-zinc-400">
                  {user.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1">
              <h2 className="font-display text-2xl font-semibold tracking-tight mb-1">
                {user.name}
              </h2>
              <p className="text-zinc-600 text-sm mb-3">{user.email}</p>
              <div className="flex items-center gap-4 text-xs text-zinc-500">
                <div>
                  <span className="font-mono uppercase tracking-wider">
                    User ID:
                  </span>{" "}
                  <span className="font-mono">{user.user_id}</span>
                </div>
                <div>
                  <span className="font-mono uppercase tracking-wider">
                    Joined:
                  </span>{" "}
                  {formatDate(user.created_at_ist || user.created_at)}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">founder type</div>
                <p className="text-sm text-zinc-600">
                  This helps me adapt my tone and what I push you on.
                </p>
              </div>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="btn btn-ghost text-sm"
                  data-testid="edit-founder-type-btn"
                >
                  Change
                </button>
              )}
            </div>

            {!editing ? (
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md">
                {currentTypeObj ? (
                  <>
                    <div className="font-display font-medium text-base mb-1">
                      {currentTypeObj.title}
                    </div>
                    <div className="text-sm text-zinc-600">
                      {currentTypeObj.description}
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-zinc-500 italic">Not set</div>
                )}
              </div>
            ) : (
              <div>
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  {FOUNDER_TYPES.map((type) => (
                    <button
                      key={type.key}
                      onClick={() => setSelectedType(type.key)}
                      data-testid={`founder-type-option-${type.key}`}
                      className={`text-left p-4 border rounded-md transition-all ${
                        selectedType === type.key
                          ? "border-zinc-950 bg-zinc-50"
                          : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <div className="font-display font-medium text-base mb-1">
                        {type.title}
                      </div>
                      <div className="text-xs text-zinc-600 leading-relaxed">
                        {type.description}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="btn btn-ghost"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !selectedType}
                    className="btn btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="save-founder-type-btn"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card p-8 mb-6">
          <div className="overline mb-1">diagnosis limits</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mb-5">
            Usage this week.
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Weekly diagnoses
              </div>
              <div className="font-display text-2xl font-semibold">
                {hasUnlimitedAccess
                  ? `${limits.weekly_used}/Unlimited`
                  : limits
                    ? `${limits.weekly_used}/${limits.weekly_limit}`
                    : "—"}
              </div>
            </div>
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Re-diagnosis cooldown
              </div>
              <div className="font-display text-2xl font-semibold">
                {hasUnlimitedAccess
                  ? "Ready now"
                  : limits
                    ? formatDuration(limits.cooldown_remaining_seconds)
                    : "—"}
              </div>
            </div>
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Last diagnosis
              </div>
              <div>
                {limits ? formatDateTime(limits.last_diagnosis_at_ist) : "—"}
              </div>
            </div>
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Next re-diagnosis
              </div>
              <div>
                {limits ? formatDateTime(limits.next_recheck_at_ist) : "—"}
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            {hasUnlimitedAccess
              ? "Unlimited access is enabled for your account. Times shown in IST."
              : "Times shown in IST. New ideas are still limited separately to one per minute."}
          </p>
        </div>

        {/* Additional Info */}
        <div className="text-xs text-zinc-500 text-center">
          <p>
            Need to update your email or delete your account?{" "}
            <a
              href="mailto:admin@softwired.in"
              className="underline hover:text-zinc-950"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </AppShell>
  );
}
